// apps/vendor/src/pages/DispatchQueue.tsx
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildTicketMessagePayload,
  composeKeyIntent,
  formatAttachmentSize,
  isImageAttachment,
  isImageUrl,
  useToast,
  type ComposeAttachment,
  type ComposeFileAttachment,
} from '@printway/ui';
import {
  useVendorDispatches, useVendorDispatch, useReplyVendorDispatch,
  useReturnVendorDispatchWork, uploadDispatchAttachment,
} from '../features/support/dispatchApi';

type ThreadAttachment = { key: string; image: boolean; url: string; label: string };

/** Chuẩn hóa attachments (typed BE) → cấu trúc render; bỏ qua phần tử lạ/legacy string. */
function toThreadAttachments(ticketId: string, atts: unknown[] | undefined): ThreadAttachment[] {
  const out: ThreadAttachment[] = [];
  for (const raw of atts ?? []) {
    if (!raw || typeof raw !== 'object') continue;
    const a = raw as { kind?: string; fileId?: string; filename?: string; contentType?: string; url?: string };
    if (a.kind === 'external-url' && a.url) {
      const ca: ComposeAttachment = { kind: 'external-url', url: a.url };
      out.push({ key: a.url, image: isImageAttachment(ca), url: a.url, label: a.url });
    } else if (a.kind === 'file' && a.fileId) {
      const ca: ComposeAttachment = {
        kind: 'file',
        fileId: a.fileId,
        filename: a.filename ?? 'attachment',
        contentType: a.contentType ?? '',
        size: 0,
      };
      out.push({
        key: a.fileId,
        image: isImageAttachment(ca),
        url: `/api/v1/tickets/${ticketId}/attachments/${a.fileId}`,
        label: a.filename ?? 'attachment',
      });
    }
  }
  return out;
}

export default function DispatchQueue(): JSX.Element {
  const { t } = useTranslation('support');
  const { toast } = useToast();
  const { data: rows = [] } = useVendorDispatches();
  const [selected, setSelected] = useState<string>('');
  const { data: detail } = useVendorDispatch(selected);
  const reply = useReplyVendorDispatch(selected);
  const returnWork = useReturnVendorDispatchWork(selected);
  const [text, setText] = useState('');

  const [files, setFiles] = useState<Array<ComposeFileAttachment & { previewUrl?: string }>>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Synchronous double-submit lock: reply.isPending only flips on the next
  // render, so held/auto-repeat Enter or Enter+Send can slip a second POST
  // through before it updates. A ref flips immediately.
  const sendingRef = useRef(false);
  const parentTicketId = detail?.dispatch.parentTicketId ?? '';
  // BE Task 4 chỉ cho vendor upload khi ticket có dispatch ACTIVE của chính
  // vendor (Dispatched|InProgress). Message vẫn post được trên dispatch
  // Returned → nếu không gate ở FE, bấm Attach trên dispatch Returned = 404
  // khó hiểu. Gate cả nút Attach lẫn paste-ảnh theo cùng điều kiện.
  const dispatchActive =
    detail?.dispatch.status === 'Dispatched' || detail?.dispatch.status === 'InProgress';

  // FE#6 — revoke pending preview blob URLs on unmount (in addition to
  // revoke-on-remove/send) so navigating away mid-compose doesn't leak them.
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(
    () => () => {
      for (const f of filesRef.current) if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    },
    [],
  );

  function send(): void {
    if (sendingRef.current) return;
    // Allow attachment-only replies: block only when there's neither text nor
    // a staged attachment (fix: dispatch-thread-reload).
    if ((text.trim().length === 0 && files.length === 0 && imageUrls.length === 0) || reply.isPending) return;
    const payload = buildTicketMessagePayload({
      content: text,
      fileAttachments: files.map(({ previewUrl: _p, ...a }) => a),
      imageUrls,
    });
    sendingRef.current = true;
    reply.mutate(payload, {
      onSuccess: () => {
        for (const f of files) if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
        setText(''); setFiles([]); setImageUrls([]);
      },
      onSettled: () => { sendingRef.current = false; },
    });
  }

  async function uploadPicked(list: FileList | File[]): Promise<void> {
    if (!parentTicketId || !dispatchActive) return; // BE Task 4 sẽ 404 nếu dispatch không active
    setUploading(true);
    try {
      for (const f of Array.from(list)) {
        try {
          const a = await uploadDispatchAttachment(parentTicketId, f);
          const previewUrl = f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined;
          setFiles((prev) => [...prev, { ...a, previewUrl }]);
        } catch (err) {
          // F3 — surface upload failures (BE returns { code } in json.error);
          // map to i18n like merchant/admin instead of failing silently.
          const code = (err as { code?: string } | null)?.code ?? '';
          toast({
            variant: 'error',
            title: t('compose.uploadFailTitle'),
            description:
              code === 'file_too_large'
                ? t('compose.fileTooLarge')
                : code === 'file_type_forbidden'
                  ? t('compose.fileTypeForbidden')
                  : undefined,
          });
        }
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function onComposeKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.repeat) return; // ignore held-key auto-repeat → no duplicate send
    if (composeKeyIntent({ key: e.key, altKey: e.altKey, shiftKey: e.shiftKey }) === 'send') {
      e.preventDefault();
      send();
    }
  }

  async function onComposePaste(e: React.ClipboardEvent<HTMLTextAreaElement>): Promise<void> {
    const imgFiles = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'));
    if (imgFiles.length > 0) { e.preventDefault(); await uploadPicked(imgFiles); return; }
    const pasted = e.clipboardData.getData('text');
    if (isImageUrl(pasted)) { e.preventDefault(); setImageUrls((prev) => [...prev, pasted.trim()]); }
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      <ul className="col-span-1 space-y-2">
        {rows.map((d) => (
          <li key={d._id}>
            <button
              className="w-full rounded border p-2 text-left"
              onClick={() => setSelected(d._id)}
            >
              <div className="text-sm">{d.scopeNote}</div>
              <div className="text-xs text-pw-text-muted">{t(`dispatch.status.${d.status}`)}</div>
            </button>
          </li>
        ))}
        {rows.length === 0 && <li className="text-sm text-pw-text-muted">{t('dispatch.empty')}</li>}
      </ul>
      <div className="col-span-2 space-y-3">
        {detail && (
          <>
            <p className="text-sm">{detail.dispatch.scopeNote}</p>
            <ul className="space-y-1">
              {detail.messages.map((m) => {
                const msgAtts = toThreadAttachments(parentTicketId, m.attachments);
                return (
                  <li key={m._id} className={m.is_vendor ? 'text-right' : 'text-left'}>
                    <span className="inline-block rounded bg-pw-surface px-2 py-1 text-sm">{m.content}</span>
                    {msgAtts.length > 0 && (
                      <ul className="mt-1 flex flex-wrap gap-2">
                        {msgAtts.map((a) =>
                          a.image ? (
                            <li key={a.key}>
                              <a href={a.url} target="_blank" rel="noopener noreferrer">
                                <img src={a.url} alt={a.label || t('compose.imageAlt')} className="max-h-40 rounded-lg object-cover" />
                              </a>
                            </li>
                          ) : (
                            <li key={a.key}>
                              <a href={a.url} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-pw-primary-strong underline">
                                {a.label} · {t('compose.download')}
                              </a>
                            </li>
                          ),
                        )}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="space-y-1">
              {(files.length > 0 || imageUrls.length > 0) && (
                <ul className="flex flex-wrap gap-2">
                  {files.map((a) => (
                    <li key={a.fileId} className="flex items-center gap-1 rounded border px-2 py-1 text-xs">
                      {a.previewUrl ? <img src={a.previewUrl} alt={a.filename} className="h-10 w-10 rounded object-cover" /> : null}
                      <span>{a.filename}</span>
                      <span className="text-pw-text-muted">({formatAttachmentSize(a.size)})</span>
                      <button type="button" aria-label={t('compose.removeAttachment', { filename: a.filename })}
                        onClick={() => setFiles((p) => { const g = p.find((x) => x.fileId === a.fileId); if (g?.previewUrl) URL.revokeObjectURL(g.previewUrl); return p.filter((x) => x.fileId !== a.fileId); })}
                        className="text-rose-600">×</button>
                    </li>
                  ))}
                  {imageUrls.map((u) => (
                    <li key={u} className="flex items-center gap-1 rounded border p-1">
                      <img src={u} alt="" className="h-10 w-10 rounded object-cover" />
                      <button type="button" aria-label={t('compose.removeAttachment', { filename: u })}
                        onClick={() => setImageUrls((p) => p.filter((x) => x !== u))} className="text-rose-600">×</button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex gap-2">
                <textarea
                  className="flex-1 resize-none rounded border px-2 py-1"
                  rows={2}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={onComposeKeyDown}
                  onPaste={(e) => void onComposePaste(e)}
                  placeholder={t('dispatch.replyPlaceholder')}
                />
                <input ref={fileInputRef} type="file" multiple className="hidden"
                  onChange={(e) => { if (e.target.files) void uploadPicked(e.target.files); }} />
                {/* Attach chỉ khi dispatch active — BE Task 4 404 upload trên dispatch Returned/Cancelled. */}
                <button type="button" className="rounded border px-3 disabled:opacity-50"
                  disabled={uploading || !dispatchActive}
                  title={!dispatchActive ? t('compose.attachDisabledHint') : undefined}
                  onClick={() => fileInputRef.current?.click()}>
                  {uploading ? t('compose.uploading') : t('compose.attach')}
                </button>
                <button className="rounded bg-pw-text px-3 text-pw-bg" onClick={send}>
                  {t('dispatch.send')}
                </button>
                {/* GIỮ nguyên nút Return của Plan 2 Task 15 (returnWork). */}
                {(detail.dispatch.status === 'Dispatched' ||
                  detail.dispatch.status === 'InProgress') && (
                  <button
                    className="rounded border px-3"
                    // fix/vendor-batch-e — only clear the note on success and
                    // surface rejections (silent-mutation sweep): a failed
                    // return used to eat the note and show nothing.
                    onClick={() => {
                      returnWork.mutate(text.trim(), {
                        onSuccess: () => setText(''),
                        onError: (e: unknown) => {
                          toast({
                            variant: 'error',
                            title: t('dispatch.toast.error'),
                            description: e instanceof Error ? e.message : undefined,
                          });
                        },
                      });
                    }}
                  >
                    {t('dispatch.return')}
                  </button>
                )}
              </div>
              <p className="text-xs text-pw-text-muted">{t('compose.keyboardHint')}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
